import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TimelineEvent {
  id: string;
  tipo: 'transicao' | 'aprovacao' | 'auditoria';
  acao: string;
  descricao: string;
  usuario_nome: string | null;
  usuario_email: string | null;
  usuario_role?: string | null;
  created_at: string;
  contrato_id: string | null;
  contrato_numero?: string | null;
  contrato_nome?: string | null;
  entidade?: string;
  entidade_id?: string;
  status_anterior?: string | null;
  status_novo?: string | null;
  observacoes?: string | null;
  dados_anteriores?: Record<string, unknown> | null;
  dados_novos?: Record<string, unknown> | null;
  dados_snapshot?: Record<string, unknown> | null;
  evidencia_ids?: string[] | null;
  etapa_nome?: string | null;
  documento_ref?: string | null;
  output_gerado?: string | null;
  is_critical?: boolean;
  hash_registro?: string | null;
  hash_anterior?: string | null;
  ip_address?: string | null;
}

const ACTION_LABELS: Record<string, string> = {
  insert: 'Criado',
  update: 'Modificado',
  delete: 'Eliminado',
  criar: 'Criado',
  alterar: 'Modificado',
  excluir: 'Eliminado',
  aprovado: 'Aprovado',
  rejeitado: 'Rejeitado',
  pendente: 'Pendente',
  transicao: 'Transição',
  transicao_estado: 'Transição de Estado',
  upload: 'Upload',
  comentario: 'Comentário',
  workflow: 'Workflow',
  materializacao_lote: 'Materialização em Lote',
  materializacao_sucesso: 'Documento Materializado',
  auto_arquivamento: 'Arquivamento Automático',
  UPDATE: 'Modificado',
  INSERT: 'Criado',
  DELETE: 'Eliminado',
  // Glosa & evidence events
  glosa_quantitativa_aplicada: 'Glosa Quantitativa',
  glosa_financeira_aplicada: 'Ajuste Financeiro',
  execucao_filha_criada: 'Reprocessamento Criado',
  anexo_adicionado: 'Evidência Adicionada',
  anexo_removido: 'Evidência Removida',
};

export const getActionLabel = (a: string) => ACTION_LABELS[a] || a;

/** Action type → color category for visual coding */
export const ACTION_COLOR_MAP: Record<string, string> = {
  insert: 'creation',
  INSERT: 'creation',
  criar: 'creation',
  aprovado: 'approval',
  rejeitado: 'rejection',
  update: 'alteration',
  UPDATE: 'alteration',
  alterar: 'alteration',
  delete: 'deletion',
  DELETE: 'deletion',
  excluir: 'deletion',
  transicao: 'alteration',
  transicao_estado: 'alteration',
  pendente: 'alteration',
  upload: 'creation',
  comentario: 'comment',
  workflow: 'alteration',
  materializacao_lote: 'creation',
  materializacao_sucesso: 'approval',
  auto_arquivamento: 'alteration',
  // Glosa & evidence events
  glosa_quantitativa_aplicada: 'rejection',
  glosa_financeira_aplicada: 'alteration',
  execucao_filha_criada: 'creation',
  anexo_adicionado: 'creation',
  anexo_removido: 'deletion',
};

export function getActionColorCategory(acao: string): string {
  return ACTION_COLOR_MAP[acao] || 'alteration';
}

const CRITICAL_ACTIONS = new Set(['aprovado', 'rejeitado', 'delete', 'DELETE']);

// ── Human-readable description mapping ──────────────────────

const ENTITY_LABELS: Record<string, string> = {
  emp_contratos: 'Contrato',
  emp_boletins_medicao: 'Boletim de Medição',
  emp_medicao_itens: 'Item de Medição',
  emp_planilha_itens: 'Item da Planilha',
  emp_planilha_grupos: 'Grupo da Planilha',
  emp_planilha_versoes: 'Versão da Planilha',
  emp_validacoes_execucao: 'Registro de Execução',
  emp_documentos: 'Documento',
  emp_documentos_versoes: 'Versão de Documento',
  emp_workflow_etapas: 'Etapa do Workflow',
  emp_workflow_aprovacoes: 'Aprovação do Workflow',
  emp_workflow_transicoes: 'Transição do Workflow',
  emp_equipes: 'Equipe',
  emp_equipe_membros: 'Membro de Equipe',
  emp_frentes_obra: 'Frente de Obra',
  emp_cronograma_atividades: 'Atividade do Cronograma',
  emp_cronograma_execucao: 'Execução do Cronograma',
  emp_cronograma_versoes: 'Versão do Cronograma',
  emp_aditivos: 'Aditivo Contratual',
  emp_auditoria: 'Log de Auditoria',
  emp_perfis_contrato: 'Perfil de Contrato',
  emp_perfis_contrato_usuario: 'Usuário do Contrato',
  emp_feature_flags: 'Flag de Funcionalidade',
  emp_snapshot_totais: 'Snapshot de Totais',
  emp_desvios: 'Desvio',
  emp_glosas: 'Glosa',
  emp_indicadores: 'Indicador',
  emp_alertas: 'Alerta',
  emp_evidencias: 'Evidência',
  execucao: 'Execução',
  addon_release: 'Release de Add-on',
};

const ACTION_HUMAN_LABELS: Record<string, string> = {
  insert: 'criado',
  INSERT: 'criado',
  criar: 'criado',
  update: 'atualizado',
  UPDATE: 'atualizado',
  alterar: 'atualizado',
  delete: 'removido',
  DELETE: 'removido',
  excluir: 'removido',
  materializacao_lote: 'materializado em lote',
  materializacao_sucesso: 'materializado com sucesso',
  auto_arquivamento: 'arquivado automaticamente',
};

const STATUS_LABELS_GLOBAL: Record<string, string> = {
  em_apontamento: 'Em Apontamento',
  aguardando_qualidade: 'Aguardando Qualidade',
  aguardando_planejamento: 'Aguardando Planejamento',
  disponivel_para_medicao: 'Disponível para Medição',
  vinculada_a_boletim: 'Vinculada a Boletim',
  aprovado: 'Aprovado',
  rejeitado: 'Rejeitado',
  cancelado: 'Cancelado',
  pendente: 'Pendente',
  rascunho: 'Rascunho',
  em_revisao: 'Em Revisão',
  finalizado: 'Finalizado',
  ativo: 'Ativo',
};

function buildHumanDescription(entidade: string, acao: string, dados_resumo?: Record<string, unknown> | null): string {
  const entityLabel = ENTITY_LABELS[entidade] || entidade;
  const actionLabel = ACTION_HUMAN_LABELS[acao] || acao;
  
  // Handle transicao_estado with de/para from dados_resumo
  if (acao === 'transicao_estado' && dados_resumo) {
    const de = dados_resumo.de as string | undefined;
    const para = dados_resumo.para as string | undefined;
    if (de && para) {
      const fromLabel = STATUS_LABELS_GLOBAL[de] || de;
      const toLabel = STATUS_LABELS_GLOBAL[para] || para;
      return `Transição: ${fromLabel} → ${toLabel}`;
    }
  }

  // Use operacao from dados_resumo if available (these are often more descriptive)
  if (dados_resumo) {
    const operacao = dados_resumo.operacao as string | undefined;
    if (operacao && !operacao.startsWith('emp_')) {
      return operacao;
    }
    // Use titulo for auto_arquivamento events
    const titulo = dados_resumo.titulo as string | undefined;
    if (titulo && (acao === 'auto_arquivamento' || acao === 'materializacao_sucesso')) {
      return `${getActionLabel(acao)}: ${titulo}`;
    }
    const numero = dados_resumo.numero_boletim || dados_resumo.numero_contrato || dados_resumo.numero || dados_resumo.codigo_item;
    const nome = dados_resumo.nome || dados_resumo.descricao || dados_resumo.etapa_nome;
    if (numero) return `${entityLabel} nº ${numero} ${actionLabel}`;
    if (nome) return `${entityLabel} "${nome}" ${actionLabel}`;
  }
  
  return `${entityLabel} ${actionLabel}`;
}

function buildTransitionDescription(status_anterior: string, status_novo: string): string {
  const from = STATUS_LABELS_GLOBAL[status_anterior] || status_anterior || '—';
  const to = STATUS_LABELS_GLOBAL[status_novo] || status_novo || '—';
  return `Transição de status: ${from} → ${to}`;
}

function buildOutputDescription(tipo: string, acao: string, status?: string | null): string | null {
  if (tipo === 'transicao') {
    return status ? `Status alterado para "${status}"` : null;
  }
  if (tipo === 'aprovacao') {
    if (acao === 'aprovado') return 'Etapa aprovada — avanço de workflow';
    if (acao === 'rejeitado') return 'Etapa rejeitada — retorno para revisão';
    return null;
  }
  // auditoria
  const map: Record<string, string> = {
    insert: 'Novo registro criado no sistema',
    INSERT: 'Novo registro criado no sistema',
    delete: 'Registro removido permanentemente',
    DELETE: 'Registro removido permanentemente',
    update: 'Dados do registro atualizados',
    UPDATE: 'Dados do registro atualizados',
    materializacao_lote: 'Documentos materializados em lote',
    materializacao_sucesso: 'Documento materializado com sucesso',
    auto_arquivamento: 'Documento arquivado automaticamente pelo sistema',
  };
  return map[acao] || 'Dados atualizados';
}

// ── End description mapping ─────────────────────────────────

export interface UseTimelineOptions {
  contratoId?: string;
  usuarioNome?: string;
  tipoAcao?: string;
  tipoEvento?: string;
  dateFrom?: string;
  dateTo?: string;
  searchText?: string;
}

const PAGE_SIZE = 50;

async function safeQuery<T>(fn: () => any): Promise<T> {
  try {
    const { data, error } = await fn();
    if (error && error.code !== 'PGRST103') {
      console.warn('[Timeline] Query warning:', error.message || error);
      return [] as unknown as T;
    }
    return (data || []) as unknown as T;
  } catch (err) {
    console.warn('[Timeline] Query exception:', err);
    return [] as unknown as T;
  }
}

async function fetchTimelinePage(
  opts: UseTimelineOptions,
  offset: number,
): Promise<{ events: TimelineEvent[]; hasMore: boolean }> {
  const { contratoId, usuarioNome, tipoAcao, dateFrom, dateTo } = opts;
  const events: TimelineEvent[] = [];

  // Action filter compatibility: skip data sources that can't match the selected action
  const TRANSITION_COMPATIBLE_ACTIONS = new Set([undefined, 'update']); // transitions are status changes (modifications)
  const APPROVAL_COMPATIBLE_ACTIONS = new Set([undefined, 'aprovado', 'rejeitado']);
  const skipTransitions = tipoAcao && !TRANSITION_COMPATIBLE_ACTIONS.has(tipoAcao);
  const skipApprovals = tipoAcao && !APPROVAL_COMPATIBLE_ACTIONS.has(tipoAcao);

  // 1) Workflow transitions — wrapped safely
  if (!skipTransitions) try {
    let tQuery = supabase
      .from('emp_workflow_transicoes' as any)
      .select('*, emp_contratos!inner(nome, numero_contrato)')
      .order('created_at', { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);

    if (contratoId) tQuery = tQuery.eq('contrato_id', contratoId);
    if (usuarioNome) tQuery = tQuery.ilike('nome_usuario', `%${usuarioNome}%`);
    if (dateFrom) tQuery = tQuery.gte('created_at', dateFrom);
    if (dateTo) tQuery = tQuery.lte('created_at', dateTo);

    const transicoes = await safeQuery<any[]>(() => tQuery);

    for (const t of transicoes) {
      const contrato = t.emp_contratos;
      const isCritical = !!t.status_novo && ['aprovado', 'rejeitado', 'cancelado'].includes(t.status_novo);
      events.push({
        id: t.id,
        tipo: 'transicao',
        acao: 'transicao',
        descricao: buildTransitionDescription(t.status_anterior, t.status_novo),
        usuario_nome: t.nome_usuario,
        usuario_email: t.email_usuario,
        created_at: t.created_at,
        contrato_id: t.contrato_id,
        contrato_numero: contrato?.numero_contrato || null,
        contrato_nome: contrato?.nome || null,
        status_anterior: t.status_anterior,
        status_novo: t.status_novo,
        observacoes: t.observacoes,
        evidencia_ids: t.evidencia_ids,
        is_critical: isCritical,
        output_gerado: buildOutputDescription('transicao', 'transicao', t.status_novo),
      });
    }
  } catch (err) {
    console.warn('[Timeline] Transition fetch error:', err);
  }

  // 2) Approval records — wrapped safely
  if (!skipApprovals) try {
    let aQuery = supabase
      .from('emp_workflow_aprovacoes' as any)
      .select('*, emp_contratos!inner(nome, numero_contrato)')
      .order('created_at', { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);

    if (contratoId) aQuery = aQuery.eq('contrato_id', contratoId);
    if (usuarioNome) aQuery = aQuery.ilike('aprovador_nome', `%${usuarioNome}%`);
    if (dateFrom) aQuery = aQuery.gte('created_at', dateFrom);
    if (dateTo) aQuery = aQuery.lte('created_at', dateTo);
    if (tipoAcao && ['aprovado', 'rejeitado', 'pendente'].includes(tipoAcao)) {
      aQuery = aQuery.eq('status', tipoAcao);
    }

    const aprovacoes = await safeQuery<any[]>(() => aQuery);

    for (const a of aprovacoes) {
      const contrato = a.emp_contratos;
      events.push({
        id: a.id,
        tipo: 'aprovacao',
        acao: a.status || 'aprovacao',
        descricao: `${a.etapa_nome || 'Etapa'}: ${getActionLabel(a.status)}`,
        usuario_nome: a.aprovador_nome,
        usuario_email: a.aprovador_email,
        created_at: a.aprovado_em || a.created_at,
        contrato_id: a.contrato_id,
        contrato_numero: contrato?.numero_contrato || null,
        contrato_nome: contrato?.nome || null,
        observacoes: a.observacoes,
        dados_snapshot: a.dados_snapshot,
        etapa_nome: a.etapa_nome,
        is_critical: CRITICAL_ACTIONS.has(a.status),
        documento_ref: a.etapa_nome || null,
        output_gerado: buildOutputDescription('aprovacao', a.status),
      });
    }
  } catch (err) {
    console.warn('[Timeline] Approval fetch error:', err);
  }

  // 3) Audit logs — wrapped safely
  try {
    let auditQuery = supabase
      .from('emp_auditoria' as any)
      .select('*')
      .order('created_at', { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);

    if (dateFrom) auditQuery = auditQuery.gte('created_at', dateFrom);
    if (dateTo) auditQuery = auditQuery.lte('created_at', dateTo);
    const ACAO_VARIANTS: Record<string, string[]> = {
      insert: ['insert', 'INSERT', 'criar'],
      update: ['update', 'UPDATE', 'alterar', 'transicao_estado'],
      delete: ['delete', 'DELETE', 'excluir'],
      aprovado: ['aprovado'],
      rejeitado: ['rejeitado'],
    };
    if (tipoAcao && ACAO_VARIANTS[tipoAcao]) {
      auditQuery = auditQuery.in('acao', ACAO_VARIANTS[tipoAcao]);
    } else if (tipoAcao) {
      auditQuery = auditQuery.eq('acao', tipoAcao);
    }

    const auditorias = await safeQuery<any[]>(() => auditQuery);

    // Resolve contract info for audit events
    const contractCache = new Map<string, { numero: string; nome: string }>();
    const auditItems = auditorias;

    const contractEntityIds = auditItems
      .filter(a => a.entidade === 'emp_contratos' && a.entidade_id)
      .map(a => a.entidade_id);

    const ENTITIES_WITH_CONTRATO = ['emp_boletins_medicao', 'emp_validacoes_execucao', 'emp_documentos', 'emp_aditivos', 'emp_planilha_versoes'];
    const entityLookups: { entidade: string; ids: string[] }[] = [];
    for (const ent of ENTITIES_WITH_CONTRATO) {
      const ids = auditItems.filter(a => a.entidade === ent && a.entidade_id).map(a => a.entidade_id);
      if (ids.length > 0) entityLookups.push({ entidade: ent, ids: [...new Set(ids)] });
    }

    const allContractIds = new Set<string>(contractEntityIds);

    for (const au of auditItems) {
      const resumo = au.dados_resumo as Record<string, unknown> | null;
      if (resumo?.contrato_id && typeof resumo.contrato_id === 'string') {
        allContractIds.add(resumo.contrato_id);
      }
    }

    const entityToContract = new Map<string, string>();
    await Promise.all(entityLookups.map(async ({ entidade, ids }) => {
      try {
        const data = await safeQuery<any[]>(() =>
          supabase.from(entidade as any).select('id, contrato_id').in('id', ids)
        );
        for (const row of data) {
          if (row.contrato_id) {
            entityToContract.set(row.id, row.contrato_id);
            allContractIds.add(row.contrato_id);
          }
        }
      } catch { /* skip */ }
    }));

    if (allContractIds.size > 0) {
      try {
        const contracts = await safeQuery<any[]>(() =>
          supabase.from('emp_contratos' as any).select('id, nome, numero_contrato').in('id', [...allContractIds])
        );
        for (const c of contracts) {
          contractCache.set(c.id, { numero: c.numero_contrato, nome: c.nome });
        }
      } catch { /* skip */ }
    }

    for (const au of auditItems) {
      const resumo = au.dados_resumo as Record<string, unknown> | null;

      let resolvedContratoId: string | null = null;
      if (au.entidade === 'emp_contratos' && au.entidade_id) {
        resolvedContratoId = au.entidade_id;
      } else if (au.entidade_id && entityToContract.has(au.entidade_id)) {
        resolvedContratoId = entityToContract.get(au.entidade_id)!;
      } else if (resumo?.contrato_id && typeof resumo.contrato_id === 'string') {
        resolvedContratoId = resumo.contrato_id;
      }

      const contractInfo = resolvedContratoId ? contractCache.get(resolvedContratoId) : null;

      if (contratoId && resolvedContratoId !== contratoId) continue;

      events.push({
        id: au.id,
        tipo: 'auditoria',
        acao: au.acao,
        descricao: buildHumanDescription(au.entidade, au.acao, resumo),
        usuario_nome: (resumo?.nome_usuario as string) || null,
        usuario_email: (resumo?.email_usuario as string) || null,
        created_at: au.created_at,
        contrato_id: resolvedContratoId,
        contrato_numero: contractInfo?.numero || null,
        contrato_nome: contractInfo?.nome || null,
        entidade: au.entidade,
        entidade_id: au.entidade_id,
        dados_anteriores: au.dados_anteriores,
        dados_novos: au.dados_novos,
        is_critical: CRITICAL_ACTIONS.has(au.acao),
        documento_ref: au.entidade_id ? `${ENTITY_LABELS[au.entidade] || au.entidade} #${au.entidade_id?.slice(0, 8)}` : null,
        output_gerado: buildOutputDescription('auditoria', au.acao),
        hash_registro: au.hash_registro || null,
        hash_anterior: au.hash_anterior || null,
        ip_address: au.ip_address || null,
      });
    }
  } catch (auditErr) {
    console.warn('[Timeline] Audit resolution error, skipping audit events:', auditErr);
  }

  // Sort merged list
  events.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const sliced = events.slice(0, PAGE_SIZE);
  return { events: sliced, hasMore: sliced.length === PAGE_SIZE };
}

/** Infinite-scroll timeline hook */
export function useTimelineDecisoes(opts: UseTimelineOptions) {
  return useInfiniteQuery({
    queryKey: ['emp-timeline-decisoes', opts.contratoId, opts.usuarioNome, opts.tipoAcao, opts.dateFrom, opts.dateTo],
    queryFn: ({ pageParam = 0 }) => fetchTimelinePage(opts, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined;
      return allPages.length * PAGE_SIZE;
    },
    initialPageParam: 0,
    staleTime: 60_000,
  });
}

/** Contracts list for filter dropdown */
export function useContratosTimeline() {
  return useQuery({
    queryKey: ['emp-timeline-contratos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_contratos' as any)
        .select('id, nome, numero_contrato')
        .order('nome');
      if (error) throw error;
      return (data || []) as unknown as { id: string; nome: string; numero_contrato: string }[];
    },
    staleTime: 300_000,
  });
}
